import type { AxisSelection } from '@atlas/types'
import { AxisCode } from '@atlas/types'
import { FilterRail, FilterRailSkeleton } from '@atlas/ui-components'
import { useFilterRailAxes } from '@/hooks/useLibrary'

/** What the rail says it does here — the same heading loaded or loading. */
const RAIL_TITLE = 'Filter assets & rules'

/**
 * The axes the hierarchy bar above already owns. Offering them here too would put two
 * controls on one scope — and `brand` and `product` are the page's identity, which a
 * multi-value filter cannot express.
 */
const HIERARCHY_AXES: string[] = [AxisCode.GEOGRAPHY, AxisCode.BRAND]

/**
 * The axis groups the rail opens with — the ones the delivered-asset grid is actually
 * browsed by. Every other pickable axis is one click away under "Add variable", so the
 * default is a starting point rather than a limit.
 */
export const DEFAULT_PRODUCT_FILTER_AXES: string[] = [AxisCode.SPECIES, AxisCode.FILE_TYPE, AxisCode.CHANNEL]

/**
 * The rail before the registry is read — used both by the rail itself and by the page
 * skeleton, so the side column has one loading shape rather than a blank block that is then
 * replaced by a second, different one for the same wait.
 */
export function ProductFilterRailSkeleton({ label }: { label?: string }) {
	return <FilterRailSkeleton title={RAIL_TITLE} groupCount={DEFAULT_PRODUCT_FILTER_AXES.length} label={label} />
}

export interface ProductFilterRailProps {
	/** Axis codes currently shown as groups. */
	shownAxisCodes: string[]
	selection: AxisSelection
	onToggle: (axisCode: string, valueCode: string) => void
	onAddAxis: (axisCode: string) => void
}

/**
 * The product page's filter rail — the same `@atlas/ui-components` rail the Library and the
 * board use, fed by the registry axes, minus the hierarchy the bar above states.
 *
 * These filters narrow what the page LISTS — every section of it, not one card; they never
 * re-resolve the page, which is why they are multi-valued and the hierarchy is not. State
 * lives in the page.
 */
export function ProductFilterRail({ shownAxisCodes, selection, onToggle, onAddAxis }: ProductFilterRailProps) {
	const { axes, isLoading } = useFilterRailAxes(shownAxisCodes)
	if (isLoading) return <ProductFilterRailSkeleton label="Loading the filters…" />
	return (
		<FilterRail
			axes={axes.filter((axis) => !HIERARCHY_AXES.includes(axis.code))}
			shownAxisCodes={shownAxisCodes}
			selection={selection}
			onToggle={onToggle}
			onAddAxis={onAddAxis}
			title={RAIL_TITLE}
		/>
	)
}
